import { Button as MaterialButton } from '@mui/material';
import { FC, useCallback, useState } from 'react';
import { ConnectedProps, connect } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router';

import { track } from '@/actions/tracking';
import { notify } from '@/actions/ui';
import { Viewer } from '@/core/viewer';
import { channelUrlToId } from '@/messaging/sendbird';
import { createChannel } from '@/messaging/store';
import { RootState } from '@/store';

import MaterialIcon from '../Icon/MaterialIcon';
import TeamAccountPromoDialog from '../TeamAccountPromo/TeamAccountPromoDialog';

const CREATE_CHANNEL_AUTH_ERROR = 'GraphQL Error: basic accounts cannot create messaging channels';

const connector = connect(
  (state: RootState) => ({
    viewer: state.viewer,
  }),
  {
    notify,
    track,
    createChannel,
  }
);

interface SendMessageButtonProps {
  viewer: Viewer;
  userId: string;
  profileSlug?: string; // Profile slug for redirecting users without billing to signup modal
  [key: string]: any;
}

const SendMessageButton: FC<SendMessageButtonProps & ConnectedProps<typeof connector>> = ({
  viewer,
  userId,
  profileSlug,
  notify,
  createChannel,
  track,
  ...other
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [creating, setCreating] = useState(false);
  const [showPromoDialog, setShowPromoDialog] = useState(false);

  const handleSendMessage = useCallback(() => {
    // If user is not logged in, show TeamAccountPromoDialog
    if (!viewer.id) {
      track('promo.show.messaging');
      setShowPromoDialog(true);
      return;
    }

    // Check if logged-in user has billing account
    const billingAccount = viewer.groups?.[0]?.billing_account;
    if (!billingAccount && profileSlug) {
      // SMB users without billing - redirect to request page to show signup modal
      if (viewer.signup_type === 'smb') {
        // Preserve query params (UTM tracking, etc.)
        const queryString = searchParams.toString();
        navigate(`/profile/${profileSlug}/request${queryString ? `?${queryString}` : ''}`);
        return;
      }
      // Non-SMB users without billing - show Contact Sales dialog
      track('promo.show.messaging');
      setShowPromoDialog(true);
      return;
    }

    setCreating(true);
    createChannel([viewer.id, userId])
      .then((channel) => {
        navigate(`/messaging/${channelUrlToId(channel.url)}`);
      })
      .catch((err: any) => {
        setCreating(false);

        if (err.message === CREATE_CHANNEL_AUTH_ERROR) {
          // Show TeamAccountPromoDialog for users without messaging permissions
          track('promo.show.messaging');
          setShowPromoDialog(true);
          return;
        }

        notify('An error occurred when starting chat with user.', 'error');
        throw err;
      });
  }, [
    createChannel,
    navigate,
    notify,
    profileSlug,
    searchParams,
    track,
    userId,
    viewer.id,
    viewer.groups,
    viewer.signup_type,
  ]);

  return (
    <>
      <MaterialButton
        size="small"
        startIcon={<MaterialIcon icon="chat" />}
        onClick={handleSendMessage}
        disabled={creating}
        {...other}
      >
        Send Message
      </MaterialButton>
      <TeamAccountPromoDialog
        open={showPromoDialog}
        onClose={() => setShowPromoDialog(false)}
        showEventTrack="promo.show.team_account.messaging"
      />
    </>
  );
};

export default connector(SendMessageButton);
